// Small "?" pill that opens the NJRC AI assistant primed with a query
// and/or topic context. Drop near a page hero so a paralegal who's
// confused about a screen has a one-click path to "explain this page
// to me" without hunting for the orb in the top nav.
//
// Usage:
//   <PageHelpHandle
//     query="What does the Today page show me, and where should I start?"
//     topicId="the-lsc-model-week"
//     label="How to use this page"
//   />
//
// Either query or topicId can be omitted. If both are present, the
// assistant opens with the topic loaded as context AND the query
// auto-sent as the first message.

interface Props {
  /** First message to auto-send to the assistant. Skip to just open the panel. */
  query?: string;
  /** Topic ID from src/client/lib/howToTopics.ts to load as context. */
  topicId?: string;
  /** Tooltip / aria-label. Defaults to "How to use this page". */
  label?: string;
  /**
   * "hero" - translucent-white pill, designed to sit on a bordeaux
   *          page-hero background.
   * "subtle" - text-muted on transparent, designed to sit on the
   *          plain page background next to a section heading.
   * Defaults to "hero".
   */
  variant?: "hero" | "subtle";
}

export function PageHelpHandle({
  query,
  topicId,
  label = "How to use this page",
  variant = "hero",
}: Props): JSX.Element {
  const onClick = () => {
    window.dispatchEvent(
      new CustomEvent("njrc:open-assistant", { detail: { query, topicId } }),
    );
  };
  const isHero = variant === "hero";
  const baseBg = isHero ? "rgba(255, 255, 255, 0.08)" : "transparent";
  const baseBorder = isHero ? "rgba(255, 255, 255, 0.18)" : "var(--color-line-hi)";
  const hoverBg = isHero ? "rgba(255, 255, 255, 0.16)" : "color-mix(in oklab, var(--color-text) 5%, transparent)";
  const hoverBorder = isHero ? "rgba(255, 255, 255, 0.32)" : "var(--color-text-muted)";
  const fg = isHero ? "rgba(255, 255, 255, 0.92)" : "var(--color-text-muted)";
  // Icon-only version of the pill. The full "How to use this page"
  // label used to live next to the ? glyph but collided with hero
  // text on tight viewports (PersonPage nickname especially). Tooltip
  // + aria-label still carry the full meaning for sighted hover users
  // and screen readers.
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 30,
        height: 30,
        borderRadius: "50%",
        background: baseBg,
        border: `1px solid ${baseBorder}`,
        color: fg,
        fontFamily: "var(--font-serif)",
        fontSize: 16,
        fontWeight: 700,
        fontStyle: "italic",
        lineHeight: 1,
        cursor: "pointer",
        transition: "background 160ms ease, border-color 160ms ease, transform 160ms ease",
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = hoverBg;
        e.currentTarget.style.borderColor = hoverBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = baseBg;
        e.currentTarget.style.borderColor = baseBorder;
      }}
    >
      ?
    </button>
  );
}
